import * as THREE from 'three';

import {
    addClouds,
    at,
    Backdrop,
    ball,
    box,
    cone,
    createKit,
    cyl,
    flat,
    glow,
    matte as toon,
    plain as inked,
    groundPlane,
    GROUND_Y,
    makeWing,
    rng,
    skyDome,
    starField,
} from './common';

const Y = GROUND_Y;
const SHORE_Z = -15;

/** A curved palm: a tapering trunk of short segments and a crown of swaying fronds. */
const palm = (
    day: boolean,
    seed: number,
    height: number,
    lean: number
): {
    group: THREE.Group;
    fronds: { pivot: THREE.Group; base: number; ph: number }[];
} => {
    const r = rng(seed);
    const group = new THREE.Group();
    const trunkA = toon(day ? 0xa8754a : 0x6d4e3a);
    const trunkB = toon(day ? 0x946238 : 0x5c4030);
    const leafA = toon(day ? 0x35c25f : 0x1f6b52);
    const leafB = toon(day ? 0x1f9e4c : 0x175543);
    const nut = toon(day ? 0x6b4423 : 0x3d2a1a);

    const n = 8;
    const pts: THREE.Vector3[] = [];
    for (let i = 0; i <= n; i++) {
        const k = i / n;
        pts.push(new THREE.Vector3(lean * k * k * height, k * height, 0));
    }
    for (let i = 0; i < n; i++) {
        const a = pts[i];
        const b = pts[i + 1];
        const len = a.distanceTo(b);
        const seg = inked(
            new THREE.Mesh(
                new THREE.CylinderGeometry(
                    0.2 - (i + 1) * 0.012,
                    0.22 - i * 0.012,
                    len * 1.04,
                    10
                ),
                i % 2 ? trunkA : trunkB
            ),
            0.06
        );
        seg.position.copy(a).add(b).multiplyScalar(0.5);
        seg.quaternion.setFromUnitVectors(
            new THREE.Vector3(0, 1, 0),
            b.clone().sub(a).normalize()
        );
        group.add(seg);
    }

    const top = pts[n];
    const crown = new THREE.Group();
    crown.position.copy(top);
    group.add(crown);
    const fronds: { pivot: THREE.Group; base: number; ph: number }[] = [];
    const count = 9;
    for (let i = 0; i < count; i++) {
        const yaw = (i / count) * Math.PI * 2 + r() * 0.2;
        const holder = new THREE.Group();
        holder.rotation.y = yaw;
        const pivot = new THREE.Group();
        const base = -0.35 - r() * 0.35;
        pivot.rotation.z = base;
        holder.add(pivot);
        // A leaf: a long flat blade, with a second one riding on it for the cartoon "feather"
        const blade = inked(ball(i % 2 ? leafA : leafB, 1.9, 0.07, 0.45), 0.08);
        blade.position.x = 1.7;
        pivot.add(blade);
        const tip = ball(i % 2 ? leafB : leafA, 1.0, 0.06, 0.3);
        tip.position.set(3.0, -0.28, 0);
        tip.rotation.z = -0.5;
        pivot.add(tip);
        crown.add(holder);
        fronds.push({ pivot, base, ph: r() * 6 });
    }
    [
        [0.22, -0.15, 0.1],
        [-0.15, -0.2, 0.2],
        [0.05, -0.25, -0.2],
    ].forEach(([x, y, z]) =>
        crown.add(at(inked(ball(nut, 0.24), 0.1), x, y, z))
    );
    return { group, fronds };
};

const umbrella = (day: boolean): THREE.Group => {
    const g = new THREE.Group();
    const pole = toon(0xf4f1ea);
    g.add(inked(at(cyl(pole, 0.07, 3.2), 0, 1.6, 0), 0.15));
    const colors = day ? [0xe63946, 0xffffff] : [0xa02a38, 0xc9cee0];
    for (let i = 0; i < 8; i++) {
        const wedge = new THREE.Mesh(
            new THREE.ConeGeometry(
                2.0,
                0.9,
                3,
                1,
                false,
                (i * Math.PI) / 4,
                Math.PI / 4
            ),
            toon(colors[i % 2], { side: THREE.DoubleSide })
        );
        wedge.position.y = 3.35;
        g.add(wedge);
    }
    g.add(at(ball(toon(0xffd23f), 0.1), 0, 3.82, 0));
    g.rotation.z = 0.12;
    return g;
};

const beachBall = (): THREE.Group => {
    const g = new THREE.Group();
    const colors = [0xe63946, 0xffffff, 0x3a86ff, 0xffd23f, 0xffffff, 0x2ec4b6];
    colors.forEach((c, i) => {
        g.add(
            new THREE.Mesh(
                new THREE.SphereGeometry(
                    0.55,
                    8,
                    12,
                    (i * Math.PI) / 3,
                    Math.PI / 3
                ),
                toon(c, { side: THREE.DoubleSide })
            )
        );
    });
    g.add(inked(ball(flat(0x000000, { visible: false }), 0.55), 0.04));
    return g;
};

const sandcastle = (day: boolean): { group: THREE.Group; flag: THREE.Mesh } => {
    const g = new THREE.Group();
    const sand = toon(day ? 0xf0c878 : 0xa88f66);
    const dark = toon(day ? 0xdcb162 : 0x907a58);
    g.add(inked(at(box(sand, 2.0, 0.9, 1.6), 0, 0.45, 0), 0.04));
    [
        [-0.95, -0.7],
        [0.95, -0.7],
        [-0.95, 0.7],
        [0.95, 0.7],
    ].forEach(([x, z]) => {
        g.add(inked(at(cyl(sand, 0.3, 1.5), x, 0.75, z), 0.07));
        g.add(at(cone(dark, 0.36, 0.45), x, 1.72, z));
    });
    g.add(inked(at(cyl(sand, 0.45, 2.0), 0, 1.4, 0), 0.06));
    g.add(at(cone(dark, 0.55, 0.65), 0, 2.72, 0));
    g.add(at(cyl(toon(0xf4f1ea), 0.03, 0.8), 0, 3.35, 0));
    const flag = at(
        box(toon(0xe63946, { side: THREE.DoubleSide }), 0.5, 0.3, 0.02),
        0.25,
        3.58,
        0
    );
    g.add(flag);
    g.add(at(box(toon(0x6b4a2b), 0.45, 0.75, 0.05), 0, 0.38, 0.84));
    return { group: g, flag };
};

const starfish = (color: number): THREE.Group => {
    const g = new THREE.Group();
    const m = toon(color);
    for (let i = 0; i < 5; i++) {
        const arm = cone(m, 0.16, 0.6);
        arm.rotation.z = Math.PI / 2;
        arm.position.x = 0.3;
        const holder = new THREE.Group();
        holder.rotation.y = (i / 5) * Math.PI * 2;
        holder.add(arm);
        g.add(holder);
    }
    g.add(at(ball(m, 0.2, 0.08, 0.2), 0, 0, 0));
    g.scale.y = 0.5;
    return g;
};

const sailboat = (): THREE.Group => {
    const g = new THREE.Group();
    const hull = inked(box(toon(0xe63946), 3.4, 0.7, 1.1), 0.05);
    hull.position.y = 0.35;
    g.add(hull);
    g.add(at(box(toon(0xffffff), 3.5, 0.18, 1.15), 0, 0.72, 0));
    g.add(at(cyl(toon(0x8a5a30), 0.06, 4.6), 0, 3, 0));
    const sail = new THREE.Shape();
    sail.moveTo(0, 0);
    sail.lineTo(0, 4);
    sail.lineTo(1.9, 0.2);
    sail.closePath();
    const main = new THREE.Mesh(
        new THREE.ShapeGeometry(sail),
        toon(0xffffff, { side: THREE.DoubleSide })
    );
    main.position.set(0.1, 1.0, 0);
    g.add(main);
    const jib = new THREE.Shape();
    jib.moveTo(0, 0);
    jib.lineTo(0, 3);
    jib.lineTo(-1.3, 0.2);
    jib.closePath();
    const front = new THREE.Mesh(
        new THREE.ShapeGeometry(jib),
        toon(0xffd23f, { side: THREE.DoubleSide })
    );
    front.position.set(-0.1, 1.0, 0);
    g.add(front);
    return g;
};

export const buildBeach = (dark: boolean): Backdrop => {
    const day = !dark;
    const kit = createKit({
        sky: day ? 0xe6f7ff : 0x7a86c4,
        ground: day ? 0xf0d9a0 : 0x3a3a60,
        hemi: day ? 1.7 : 1.0,
        sun: day ? 0xfff3d6 : 0x9fb4ff,
        sunIntensity: day ? 2.6 : 1.2,
    });
    const { scene, root, ticks } = kit;
    const r = rng(7);

    scene.add(
        day
            ? skyDome(0x2c86e6, 0x6fc3ff, 0xdff6ff)
            : skyDome(0x050722, 0x1a2866, 0x4a5fa8)
    );

    // ---- sand and sea
    const sandColor = day ? 0xf6dc96 : 0x8d84a0;
    root.add(groundPlane(sandColor, 14, SHORE_Z));
    const wet = new THREE.Mesh(
        new THREE.PlaneGeometry(400, 2.6),
        toon(day ? 0xdfbf7a : 0x6c6482)
    );
    wet.rotation.x = -Math.PI / 2;
    wet.position.set(0, Y + 0.005, SHORE_Z + 1.3);
    root.add(wet);

    const seaMat = toon(day ? 0x22b5d9 : 0x143a6e);
    const sea = new THREE.Mesh(new THREE.PlaneGeometry(900, 500), seaMat);
    sea.rotation.x = -Math.PI / 2;
    sea.position.set(0, Y + 0.002, SHORE_Z - 250);
    root.add(sea);
    const deep = new THREE.Mesh(
        new THREE.PlaneGeometry(900, 180),
        toon(day ? 0x1495c4 : 0x0e2a55)
    );
    deep.rotation.x = -Math.PI / 2;
    deep.position.set(0, Y + 0.004, SHORE_Z - 360);
    root.add(deep);

    // Foam that rolls in and out
    const foamMat = flat(0xffffff, { transparent: true, opacity: 0.9 });
    const foam = [0, 1, 2].map((i) => {
        const f = new THREE.Mesh(
            new THREE.PlaneGeometry(400, 0.55 - i * 0.1),
            foamMat.clone()
        );
        f.rotation.x = -Math.PI / 2;
        root.add(f);
        return f;
    });
    ticks.push((t) => {
        foam.forEach((f, i) => {
            const p = (Math.sin(t * 0.7 + i * 1.7) + 1) / 2;
            f.position.set(
                0,
                Y + 0.02 + i * 0.002,
                SHORE_Z + 0.4 - i * 0.9 + p * 1.2
            );
            (f.material as THREE.MeshBasicMaterial).opacity = 0.35 + p * 0.65;
        });
    });

    // Light dashes on the water
    const lightWater = flat(day ? 0x9ae8f5 : 0x3c6ab0);
    const dashes: { m: THREE.Mesh; x: number; ph: number }[] = [];
    for (let i = 0; i < 70; i++) {
        const z = SHORE_Z - 6 - Math.pow(r(), 1.7) * 140;
        const w = 1.5 + r() * 3 + -z / 40;
        const m = box(lightWater, w, 0.02, 0.1 + -z * 0.004);
        const x = (r() * 2 - 1) * (30 + -z * 0.9);
        m.position.set(x, Y + 0.012, z);
        root.add(m);
        dashes.push({ m, x, ph: r() * 6 });
    }
    ticks.push((t) => {
        dashes.forEach(({ m, x, ph }) => {
            m.position.x = x + Math.sin(t * 0.3 + ph) * 1.2;
            m.scale.x = 1 + Math.sin(t * 1.1 + ph) * 0.3;
        });
    });

    // Pebbles, shells and dune grass so the sand is not an empty sheet
    {
        const pebbleGeo = new THREE.IcosahedronGeometry(0.12, 0);
        const pebbles = new THREE.InstancedMesh(pebbleGeo, toon(0xffffff), 140);
        const tones = (
            day
                ? [0xe8c887, 0xffffff, 0xd9b26e, 0xf4a6b8]
                : [0x7a7090, 0xb8b4cc, 0x6a6080, 0xa0708a]
        ).map((c) => new THREE.Color(c));
        const m4 = new THREE.Matrix4();
        for (let i = 0; i < 140; i++) {
            const sc = 0.6 + r() * 1.4;
            m4.makeScale(sc, sc * 0.6, sc);
            m4.setPosition((r() * 2 - 1) * 30, Y + 0.04, 8 - r() * 22);
            pebbles.setMatrixAt(i, m4);
            pebbles.setColorAt(i, tones[Math.floor(r() * tones.length)]);
        }
        pebbles.frustumCulled = false;
        root.add(pebbles);

        const tuftGeo = new THREE.ConeGeometry(0.09, 0.9, 4);
        const tufts = new THREE.InstancedMesh(
            tuftGeo,
            toon(day ? 0x5fc46a : 0x2f7a58),
            160
        );
        let n = 0;
        while (n < 160) {
            const side = r() > 0.5 ? 1 : -1;
            const cx = side * (9 + r() * 22);
            const cz = -3 - r() * 11;
            for (let k = 0; k < 4 && n < 160; k++, n++) {
                m4.makeRotationZ((r() - 0.5) * 0.5);
                m4.setPosition(
                    cx + (r() - 0.5) * 0.8,
                    Y + 0.4,
                    cz + (r() - 0.5) * 0.8
                );
                tufts.setMatrixAt(n, m4);
            }
        }
        tufts.frustumCulled = false;
        root.add(tufts);
    }

    // ---- the sun or the moon
    if (day) {
        const sun = ball(flat(0xfff1a8), 18);
        sun.position.set(-90, 34, -360);
        root.add(sun);
        const halo = glow('rgba(255,236,150,1)', 170, 0.9);
        halo.position.copy(sun.position);
        root.add(halo);
    } else {
        const moon = ball(flat(0xf6f1d6), 15);
        moon.position.set(90, 34, -360);
        root.add(moon);
        const halo = glow('rgba(200,215,255,1)', 140, 0.8);
        halo.position.copy(moon.position);
        root.add(halo);
        [
            [-3, 3, 2.4],
            [4, -2, 1.8],
            [0, -5.5, 1.3],
        ].forEach(([x, y, rad]) => {
            const crater = ball(flat(0xd9d2b4), rad, rad, 0.2);
            crater.position.set(
                moon.position.x + x,
                moon.position.y + y,
                moon.position.z + 14.6
            );
            root.add(crater);
        });
        scene.add(starField(600, 380, 9, { upperOnly: true }));
        // The moon's road on the water
        const road = new THREE.Mesh(
            new THREE.PlaneGeometry(14, 160),
            flat(0xdfe8ff, { transparent: true, opacity: 0.18 })
        );
        road.rotation.x = -Math.PI / 2;
        road.position.set(30, Y + 0.02, -110);
        root.add(road);
    }

    // ---- an island on the horizon
    {
        const isle = new THREE.Group();
        const mound = ball(toon(day ? 0x4fb86a : 0x1f5a45), 26, 7, 12);
        isle.add(mound);
        isle.add(
            at(ball(toon(day ? 0xf0d28a : 0x8d84a0), 34, 2, 16), 0, -2.2, 3)
        );
        const p1 = palm(day, 3, 8, 0.2);
        p1.group.position.set(-6, 5, 2);
        p1.group.scale.setScalar(1.4);
        isle.add(p1.group);
        isle.position.set(-95, Y + 1, -210);
        root.add(isle);
        ticks.push((t) => {
            p1.fronds.forEach(
                (f) =>
                    (f.pivot.rotation.z =
                        f.base + Math.sin(t * 1.2 + f.ph) * 0.05)
            );
        });
    }

    // ---- boat
    const boat = sailboat();
    boat.scale.setScalar(2.2);
    root.add(boat);
    ticks.push((t) => {
        const x = ((t * 1.6 + 60) % 240) - 120;
        boat.position.set(x, Y + 0.1 + Math.sin(t * 1.4) * 0.12, -64);
        boat.rotation.z = Math.sin(t * 1.4) * 0.05;
        boat.rotation.y = 0.15;
    });

    // ---- palms
    const palms = [
        { x: -10, z: -16, h: 6.4, lean: 0.2, s: 1 },
        { x: 11, z: -20, h: 7.4, lean: -0.2, s: 1 },
        { x: -24, z: -24, h: 7, lean: 0.15, s: 1 },
        { x: 25, z: -12, h: 5.8, lean: -0.25, s: 1 },
    ];
    const palmFronds: ReturnType<typeof palm>['fronds'] = [];
    palms.forEach((p, i) => {
        const built = palm(day, 40 + i, p.h, p.lean);
        built.group.position.set(p.x, Y, p.z);
        root.add(built.group);
        palmFronds.push(...built.fronds);
    });
    ticks.push((t) => {
        palmFronds.forEach((f) => {
            f.pivot.rotation.z = f.base + Math.sin(t * 1.3 + f.ph) * 0.06;
        });
    });

    // ---- the beach stuff
    const um = umbrella(day);
    um.position.set(9, Y, -5);
    root.add(um);
    const towel = new THREE.Group();
    ['#ff7b9c', '#ffffff'].forEach((c, i) => {
        for (let k = 0; k < 4; k++) {
            if ((k + i) % 2 === 0)
                towel.add(
                    at(
                        box(toon(i ? 0xffffff : 0xff7b9c), 0.5, 0.05, 1.7),
                        -0.75 + k * 0.5,
                        0.03,
                        0
                    )
                );
        }
    });
    towel.position.set(7.2, Y, -2.6);
    towel.rotation.y = -0.4;
    root.add(towel);

    const bb = beachBall();
    root.add(bb);
    ticks.push((t) => {
        const p = Math.abs(Math.sin(t * 1.9));
        bb.position.set(4.4, Y + 0.55 + p * 1.1, -1.4);
        bb.rotation.set(t * 1.2, t * 0.9, 0);
        const squash = 1 - Math.max(0, 0.15 - p) * 1.5;
        bb.scale.set(1 / Math.sqrt(squash), squash, 1 / Math.sqrt(squash));
    });

    const castle = sandcastle(day);
    castle.group.position.set(-7.2, Y, -4);
    castle.group.scale.setScalar(0.75);
    root.add(castle.group);
    ticks.push((t) => {
        castle.flag.rotation.y = Math.sin(t * 5) * 0.4;
    });

    const star1 = starfish(day ? 0xff8a3d : 0xb8643a);
    star1.position.set(2.8, Y + 0.05, 0.2);
    star1.rotation.y = 0.5;
    root.add(star1);
    const star2 = starfish(day ? 0xff6f91 : 0xa84a64);
    star2.position.set(-2.9, Y + 0.05, 0.8);
    star2.scale.multiplyScalar(0.8);
    root.add(star2);

    // ---- sky things
    const cloudMat = toon(day ? 0xffffff : 0x56659f, {
        emissive: day ? 0x000000 : 0x1a2250,
    });
    ticks.push(
        addClouds(root, cloudMat, day ? 12 : 6, 31, {
            x: 220,
            yMin: 8,
            yMax: 22,
            zMin: 120,
            zMax: 220,
        })
    );

    if (day) {
        const gulls: {
            g: THREE.Group;
            wings: ReturnType<typeof makeWing>[];
            a: number;
            cx: number;
            cy: number;
            sp: number;
        }[] = [];
        for (let i = 0; i < 4; i++) {
            // A seagull flies along its local +z, wings spread along x.
            const g = new THREE.Group();
            const white = flat(0xffffff);
            const beak = flat(0xffb627);
            g.add(inked(at(ball(white, 0.2, 0.18, 0.55), 0, 0, 0), 0.1));
            g.add(inked(at(ball(white, 0.15), 0, 0.1, 0.55), 0.12));
            const bill = at(cone(beak, 0.05, 0.22), 0, 0.07, 0.8);
            bill.rotation.x = Math.PI / 2;
            g.add(bill);
            g.add(at(box(white, 0.22, 0.05, 0.3), 0, 0.02, -0.6));
            // White arm and coverts, pale grey flight feathers, black-tipped primaries.
            const wingMat = flat(0xffffff, {
                vertexColors: true,
                side: THREE.DoubleSide,
            });
            const palette = {
                cover: 0xffffff,
                flight: 0xc9d3de,
                tip: 0x2a2a35,
            };
            const wings = [-1, 1].map((side) => {
                const wing = makeWing(side, palette, wingMat);
                wing.shoulder.position.set(side * 0.15, 0.04, 0.05);
                g.add(wing.shoulder);
                return wing;
            });
            g.scale.setScalar(1.6);
            root.add(g);
            gulls.push({
                g,
                wings,
                a: r() * 6.28,
                cx: -25 + i * 18,
                cy: Y + 7 + r() * 4,
                sp: 0.3 + r() * 0.2,
            });
        }
        ticks.push((t) => {
            gulls.forEach((s, i) => {
                const a = s.a + t * s.sp;
                s.g.position.set(
                    s.cx + Math.cos(a) * 13,
                    s.cy + Math.sin(a * 2) * 1.2,
                    -50 + Math.sin(a) * 9
                );
                // Face along the flight path (the ellipse's tangent).
                s.g.rotation.y = Math.atan2(-13 * Math.sin(a), 9 * Math.cos(a));
                s.g.rotation.z = Math.cos(a) * 0.12;
                // The wrist trails the shoulder, so the tips whip at the end of each stroke.
                s.wings.forEach(({ shoulder, tip, side }) => {
                    shoulder.rotation.z = side * Math.sin(t * 6 + i) * 0.5;
                    tip.rotation.z = side * Math.sin(t * 6 + i - 0.9) * 0.35;
                });
            });
        });
    } else {
        // A campfire to keep the night friendly
        const fire = new THREE.Group();
        const log = toon(0x6b4423);
        [0, 1, 2].forEach((i) => {
            const l = inked(cyl(log, 0.14, 1.5), 0.1);
            l.rotation.z = Math.PI / 2;
            l.rotation.y = (i * Math.PI) / 3;
            l.position.y = 0.16;
            fire.add(l);
        });
        const flames = [
            { c: 0xff5a1f, h: 1.5, r: 0.5 },
            { c: 0xff9d2e, h: 1.15, r: 0.36 },
            { c: 0xffe066, h: 0.75, r: 0.22 },
        ].map((f) => {
            const m = cone(flat(f.c), f.r, f.h);
            m.position.y = 0.2 + f.h / 2;
            fire.add(m);
            return m;
        });
        fire.position.set(-9.5, Y, -0.5);
        root.add(fire);
        const light = new THREE.PointLight(0xff9a3c, 16, 18, 1.6);
        light.position.set(-9.5, Y + 1.2, 0.3);
        root.add(light);
        ticks.push((t) => {
            flames.forEach((m, i) => {
                m.scale.y = 1 + Math.sin(t * 9 + i * 2) * 0.18;
                m.rotation.z = Math.sin(t * 6 + i) * 0.08;
            });
            light.intensity = 14 + Math.sin(t * 11) * 3 + Math.sin(t * 17) * 2;
        });
    }

    return kit.finish(false);
};
